// The DEFAULT data source: serves the in-memory domain fixtures behind the
// `PortalDataSource` contract. No network, no env, no Supabase — this is what
// keeps the existing tests and the fixtures-backed UI working unchanged.
//
// It returns children as `ServerChild` (id + owner only), dropping the fixture
// `displayLabel`, so the in-memory path honours the exact same zero-PII shape
// the Supabase backend is constrained to. The label is layered in client-side.

import {
  accounts,
  assignments,
  children,
  childLinks,
  consents,
  license,
  memberships,
  organisation,
  seats,
  sessionSummaries,
  skillSummaries,
  stepStates,
  supportProfiles,
} from "../domain/fixtures";
import { normaliseNote } from "../domain/assignmentNote";
import type {
  PortalDataSource,
  ServerChild,
  OrgConsentEntry,
  OrgOverview,
  OrgMemberEntry,
  OrgRosterEntry,
  SharingStatus,
} from "./types";
import type { ConsentScopeKey } from "../domain/types";
import type { Sponsorship } from "../domain/sponsorship";
import {
  DEMO_NOW_MS,
  demoAllowance,
  demoBillingAndConsole,
  demoLicences,
  fixtureChildRef,
} from "./fixturesDemo";

const serverChildren: ServerChild[] = children.map((c) => ({
  id: c.id,
  ownerAccountId: c.ownerAccountId,
}));

// Monotonic counter so simulated writes return distinct-but-deterministic ids
// (no Date.now, so results are stable across test runs).
let _seq = 0;
const simId = (prefix: string): string => `${prefix}-fixture-${++_seq}`;

const FIXTURE_OWNER = accounts[0]?.id ?? "acc-lena";

// --- Admin aggregates, derived from the same fixtures --------------------------
//
// These mirror what the admin-only `security definer` RPCs compute live, INCLUDING
// the opaque child ref: the offline path must not hand the UI a child UUID that
// the live path would never give it, or a screen could be built against data that
// only exists in the demo.


/** Viewer links, grouped by child — the unit both admin aggregates work from. */
function viewerLinksByChild() {
  const byChild = new Map<string, typeof childLinks>();
  for (const l of childLinks) {
    if (l.relation !== "viewer") continue;
    const list = byChild.get(l.childId) ?? [];
    list.push(l);
    byChild.set(l.childId, list);
  }
  return byChild;
}

function statusFor(
  links: typeof childLinks,
): { status: SharingStatus; lastChange: string | null } {
  let status: SharingStatus = "pending";
  let lastChange: string | null = null;
  let sawRevoked = false;
  for (const l of links) {
    const consent = l.consentId ? consents.find((c) => c.id === l.consentId) : null;
    if (!consent) continue;
    if (!lastChange || consent.grantedAt > lastChange) lastChange = consent.grantedAt;
    if (consent.revokedAt) sawRevoked = true;
    else if (l.scope.length > 0) status = "active";
  }
  if (status !== "active" && sawRevoked) status = "revoked";
  return { status, lastChange };
}

const FIXTURE_ROSTER: OrgRosterEntry[] = [...viewerLinksByChild()].map(
  ([childId, links]) => {
    const { status, lastChange } = statusFor(links);
    return {
      childRef: fixtureChildRef(childId),
      supportedBy: links
        .map((l) => accounts.find((a) => a.id === l.accountId)?.email)
        .filter((e): e is string => !!e)
        .sort(),
      sharingStatus: status,
      lastChange,
    };
  },
);

const FIXTURE_CONSENT_LOG: OrgConsentEntry[] = consents
  .map((consent) => {
    const link = childLinks.find((l) => l.consentId === consent.id);
    if (!link) return null;
    return {
      consentId: consent.id,
      childRef: fixtureChildRef(link.childId),
      memberEmail: accounts.find((a) => a.id === link.accountId)?.email ?? "—",
      scope: consent.scope as ConsentScopeKey[],
      status: (consent.revokedAt
        ? "revoked"
        : link.scope.length > 0
          ? "active"
          : "pending") as SharingStatus,
      grantedAt: consent.grantedAt,
      revokedAt: consent.revokedAt ?? null,
    };
  })
  .filter((e): e is OrgConsentEntry => e !== null)
  .sort((a, b) => b.grantedAt.localeCompare(a.grantedAt));

const FIXTURE_MEMBERS: OrgMemberEntry[] = memberships.map((m) => ({
  accountId: m.accountId,
  email: accounts.find((a) => a.id === m.accountId)?.email ?? m.accountId,
  role: m.role,
  seatStatus: seats.find((s) => s.accountId === m.accountId)?.status ?? null,
  joinedAt: accounts.find((a) => a.id === m.accountId)?.createdAt ?? "",
}));

const FIXTURE_OVERVIEW: OrgOverview = {
  orgName: organisation.name,
  orgType: organisation.type,
  seatsTotal: license.seatsTotal,
  seatsUsed: license.seatsUsed,
  memberCount: memberships.length,
  adminCount: memberships.filter((m) => m.role === "admin").length,
  linkedChildCount: FIXTURE_ROSTER.length,
  pendingInviteCount: 1,
};

// --- Family sponsorship fixtures ---------------------------------------------
//
// Four grants, one in each state, so the demo screen shows the whole vocabulary
// at once: pending, active, lapsed, revoked.
//
// The dates are FIXED, which means the state each row lands in depends on what
// "now" is. Rather than let that drift with the calendar — and quietly turn the
// demo into four lapsed rows next year — the demo path evaluates them against
// {@link FIXTURE_SPONSORSHIP_NOW}, a frozen instant these dates were written
// around. Deterministic forever, no Date.now(), same discipline as `simId`.
//
// Every value here is an ADULT EMAIL. There is no child anywhere in this shape,
// and there must never be one.

/**
 * The instant the demo path evaluates sponsorship statuses at. The fixture dates
 * below are written relative to it, so the demo mix stays pending / active /
 * lapsed / revoked no matter when the demo is opened.
 */
export const FIXTURE_SPONSORSHIP_NOW = "2026-07-27T00:00:00.000Z";

/** Frozen demo epoch in milliseconds — pass this as `nowMs` on the demo path. */
export const FIXTURE_SPONSORSHIP_NOW_MS = Date.parse(FIXTURE_SPONSORSHIP_NOW);

/** How many families the demo organisation's licence permits at once. */
export const FIXTURE_FAMILY_ALLOWANCE = 5;

/** Newest first, exactly as `listSponsorships` returns them. */
export const FIXTURE_SPONSORSHIPS: readonly Sponsorship[] = [
  {
    // PENDING — granted, but this family has not signed in yet.
    id: "sp-fixture-pending",
    email: "hana.rahman@example.com",
    endsAt: "2027-06-30T00:00:00.000Z",
    grantedAt: "2026-07-20T10:15:00.000Z",
    claimedAt: null,
    revokedAt: null,
  },
  {
    // REVOKED — an admin ended it early. Kept as an audit row, never deleted.
    id: "sp-fixture-revoked",
    email: "daniel.osei@example.com",
    endsAt: "2027-06-30T00:00:00.000Z",
    grantedAt: "2026-07-05T09:00:00.000Z",
    claimedAt: "2026-07-06T18:40:00.000Z",
    revokedAt: "2026-07-18T11:05:00.000Z",
  },
  {
    // ACTIVE — claimed, and still inside its window.
    id: "sp-fixture-active",
    email: "priya.menon@example.com",
    endsAt: "2027-06-30T00:00:00.000Z",
    grantedAt: "2026-06-30T08:30:00.000Z",
    claimedAt: "2026-07-01T07:12:00.000Z",
    revokedAt: null,
  },
  {
    // LAPSED — last term's grant. No write happened; the end date simply passed.
    id: "sp-fixture-lapsed",
    email: "aminah.yusof@example.com",
    endsAt: "2026-07-15T00:00:00.000Z",
    grantedAt: "2026-04-02T14:00:00.000Z",
    claimedAt: "2026-04-03T09:20:00.000Z",
    revokedAt: null,
  },
];

/** Fixtures-backed implementation of {@link PortalDataSource}. */
export const fixturesDataSource: PortalDataSource = {
  organisations: () => Promise.resolve([organisation]),
  accounts: () => Promise.resolve(accounts),
  memberships: () => Promise.resolve(memberships),
  children: () => Promise.resolve(serverChildren),
  childLinks: () => Promise.resolve(childLinks),
  consents: () => Promise.resolve(consents),
  sessionSummaries: () => Promise.resolve(sessionSummaries),
  skillSummaries: () => Promise.resolve(skillSummaries),
  assignments: () => Promise.resolve(assignments),
  stepStates: () => Promise.resolve(stepStates),
  supportProfiles: () => Promise.resolve(supportProfiles),
  licenses: () => Promise.resolve([license]),
  seats: () => Promise.resolve(seats),
  memberInvitations: () =>
    Promise.resolve([
      {
        id: "inv-fixture-sarah",
        orgId: organisation.id,
        email: "sarah.chen@brightops.org",
        role: "member",
        invitedBy: FIXTURE_OWNER,
        expiresAt: "2026-07-01T00:00:00.000Z",
        createdAt: "2026-06-17T09:00:00.000Z",
      },
    ]),

  // --- Writes: simulated, no network (keeps the offline UI + tests believable) --
  generateLinkCode: () => Promise.resolve("BRIGHTSTEP"),
  claimLinkCode: () => Promise.resolve(simId("link")),
  grantConsent: () => Promise.resolve(simId("consent")),
  revokeConsent: () => Promise.resolve(),
  purgeSharedData: () => Promise.resolve(),
  allocateSeat: () => Promise.resolve(),
  revokeSeat: () => Promise.resolve(),
  inviteMember: () => Promise.resolve(simId("inv")),
  removeMember: () => Promise.resolve(),
  cancelInvite: () => Promise.resolve(),

  // --- Organisation onboarding (simulated) -------------------------------------
  createOrganisation: () => Promise.resolve(simId("org")),
  acceptMemberInvitations: () => Promise.resolve(0),
  setMemberRole: () => Promise.resolve(),
  updateOrganisation: () => Promise.resolve(),

  // --- Admin aggregate reads ---------------------------------------------------
  orgOverview: () => Promise.resolve(FIXTURE_OVERVIEW),
  orgMembers: () => Promise.resolve(FIXTURE_MEMBERS),
  orgRoster: () => Promise.resolve(FIXTURE_ROSTER),
  orgConsentLog: () => Promise.resolve(FIXTURE_CONSENT_LOG),
  orgActivity: () =>
    Promise.resolve([
      { id: "a1", kind: "member", text: "Marcus Bell joined as admin", at: "2026-06-18T09:30:00.000Z" },
      { id: "a2", kind: "seat", text: "Seat assigned to Dr Priya Nair", at: "2026-06-18T09:28:00.000Z" },
      { id: "a3", kind: "invite", text: "New member invited: sarah.chen@brightops.org", at: "2026-06-17T09:00:00.000Z" },
      { id: "a4", kind: "seat", text: "Seat assigned to Marcus Bell", at: "2026-06-14T11:00:00.000Z" },
      { id: "a5", kind: "org", text: "Organisation created", at: "2026-06-12T08:00:00.000Z" },
    ]),

  // --- Family sponsorship (simulated) ------------------------------------------
  //
  // Two of the four fixtures count against the allowance (the pending one and
  // the active one), so the demo shows "2 of 5 sponsorships used" — some used,
  // some left. That is the interesting middle case; zero-allowance and
  // allowance-reached are reachable from the live path and covered by tests.
  listSponsorships: () => Promise.resolve(demoLicences().map((s) => ({ ...s }))),
  getFamilyAllowance: () => Promise.resolve(demoAllowance()),
  grantSponsorship: (_orgId, email, endsAt) => {
    const id = simId("sponsorship");
    demoLicences().unshift({
      id,
      email: email.trim().toLowerCase(),
      endsAt,
      grantedAt: new Date(DEMO_NOW_MS).toISOString(),
      claimedAt: null,
      revokedAt: null,
    });
    return Promise.resolve(id);
  },
  revokeSponsorship: (id) => {
    const row = demoLicences().find((s) => s.id === id);
    if (row && !row.revokedAt) row.revokedAt = new Date(DEMO_NOW_MS).toISOString();
    return Promise.resolve();
  },

  ...demoBillingAndConsole,

  // --- Push: nothing to send without a backend ---------------------------------
  notifyFamily: () => Promise.resolve(),
};
